/**
 * WaOrderDetailsModal — pantalla "Solicitud de pedido" estilo WhatsApp Business
 * nativo (lado vendedor). Layout calcado del native app:
 *
 *   Header: "Solicitud de pedido"
 *   From: [avatar] Nombre del cliente — fecha
 *   ─────────────────────────────────────────
 *   [thumb] Producto         qty 1   S/ 21.00
 *   [thumb] Producto         qty 1   S/ 18.00
 *   ─────────────────────────────────────────
 *   Total                            S/ 39.00
 *   ─────────────────────────────────────────
 *   [ Aceptar pedido ]
 *
 * Construido 100% con Polaris donde se puede: Thumbnail, Avatar, BlockStack,
 * InlineStack, Card, Box, Divider, Banner, Spinner, Text, Button, Modal.
 */

import { useEffect, useState } from 'react';
import {
  Modal,
  Text,
  BlockStack,
  InlineStack,
  Spinner,
  Banner,
  Avatar,
  Box,
  Divider,
  SkeletonThumbnail,
  SkeletonBodyText,
} from '@shopify/polaris';
import { supabase } from '@/lib/supabase';
import { getAvatarColor } from './wa-helpers';

/**
 * Thumbnail con fallback elegante cuando la URL del CDN de WhatsApp expira.
 *
 * Por qué: las URLs `mmg.whatsapp.net/...` que vienen en `imageUrl` de cada
 * item del orderMessage son tokens efímeros del CDN de Meta. Pasados
 * unos días/horas pueden 404 o quedar bloqueadas por CORS — Polaris
 * Thumbnail renderiza el ícono "imagen rota" del browser, feo.
 *
 * Patrón: si la <img> falla (o si la URL viene vacía), mostramos un
 * cuadrado del mismo tamaño con la primera letra del producto sobre el
 * color hasheado del nombre (mismo helper que avatares de contacto).
 * Consistente, sin red, sin layout shift.
 *
 * Estado React, NO mutación DOM — un `el.parentElement.textContent` directo
 * confunde la reconciliación cuando cambia el src.
 */
function OrderItemThumbnail({ src, alt }: { src?: string; alt: string }) {
  const [errored, setErrored] = useState(false);
  const showImg = !!src && !errored;
  if (showImg) {
    return (
      <img
        key={src}
        src={src}
        alt={alt}
        style={{
          width: 60, height: 60, borderRadius: 8, objectFit: 'cover',
          flexShrink: 0, background: 'var(--p-color-bg-surface-secondary)',
        }}
        onError={() => setErrored(true)}
      />
    );
  }
  const initial = (alt?.trim()?.[0] ?? '?').toUpperCase();
  const bg = getAvatarColor(alt);
  return (
    <div
      role="img"
      aria-label={alt}
      style={{
        width: 60, height: 60, borderRadius: 8, flexShrink: 0,
        background: bg, color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 24, fontWeight: 600,
      }}
    >
      {initial}
    </div>
  );
}

interface OrderProduct {
  id?: string;
  retailerId?: string;
  name?: string;
  quantity?: number;
  price?: number | string;
  currency?: string;
  imageUrl?: string;
  productImageCount?: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
  orderId: string;
  orderToken: string;
  /** Total del orderMessage en sub-unidades ×1000. */
  totalAmount1000?: number;
  currency?: string;
  /** Timestamp ISO del orderMessage (msg.created_at). */
  orderDate: string;
  /** Items pre-fetched (Feature 5c-B via realtime). Si llegan, render
   *  instantáneo SIN tocar el edge function. */
  prefetchedItems?: Array<Record<string, unknown>>;
  /** Datos del cliente desde memoria (selectedConv) — instant render.
   *  Si llegan, NO se hace fetch. Patrón "memoria primero" feedback. */
  customerName?: string | null;
  customerPhone?: string;
  customerPhotoUrl?: string | null;
  /** Callback para crear pedido COD interno con estos items pre-cargados.
   *  Si no se provee, el botón "Crear pedido COD" queda deshabilitado. */
  onCreateInternalOrder?: (params: {
    items: OrderProduct[];
    totalAmount1000?: number;
    currency?: string;
  }) => void;
  /** Callback para enviar respuesta rápida pre-formateada al cliente.
   *  Baileys no soporta "Aceptar pedido" formal, pero un mensaje de texto
   *  comunica lo mismo al cliente. */
  onSendQuickReply?: (text: string) => void;
}

function formatPrice(price?: number | string, currency?: string): string {
  if (price == null || price === '') return '';
  const n = typeof price === 'number' ? price : Number(price);
  if (!Number.isFinite(n)) return '';
  // Baileys getOrderDetails devuelve price en sub-unidades ×1000.
  const decimal = n / 1000;
  const cur = currency ?? '';
  return `${cur} ${decimal.toFixed(2)}`.trim();
}

function formatTotalAmount1000(amount1000?: number, currency?: string): string {
  if (typeof amount1000 !== 'number' || amount1000 <= 0) return '';
  const cur = currency ?? '';
  return `${cur} ${(amount1000 / 1000).toFixed(2)}`.trim();
}

function formatOrderDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-PE', {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function WaOrderDetailsModal({
  isOpen,
  onClose,
  conversationId,
  orderId,
  orderToken,
  totalAmount1000,
  currency,
  orderDate,
  prefetchedItems,
  customerName,
  customerPhone,
  customerPhotoUrl,
  onCreateInternalOrder,
  onSendQuickReply,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<OrderProduct[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Customer: si el padre pasó los datos desde memoria (selectedConv),
  // los usamos instantáneo. Solo fetch como fallback si nada vino.
  const [fetchedCustomer, setFetchedCustomer] = useState<{ name: string | null; phone: string; avatar?: string | null } | null>(null);
  const customer = customerName || customerPhone
    ? { name: customerName ?? null, phone: customerPhone ?? '', avatar: customerPhotoUrl ?? null }
    : fetchedCustomer;

  // Fallback fetch: solo si el padre NO pasó los datos desde memoria.
  useEffect(() => {
    if (!isOpen || !conversationId) return;
    if (customerName || customerPhone) return; // memoria primero: skip fetch
    let cancelled = false;
    void (async () => {
      const { data } = await supabase
        .from('wa_conversations')
        .select('contact_name, contact_phone, contact_photo_url')
        .eq('id', conversationId)
        .maybeSingle();
      if (cancelled || !data) return;
      const row = data as { contact_name: string | null; contact_phone: string; contact_photo_url?: string | null };
      setFetchedCustomer({ name: row.contact_name, phone: row.contact_phone, avatar: row.contact_photo_url });
    })();
    return () => { cancelled = true; };
  }, [isOpen, conversationId, customerName, customerPhone]);

  // Fetch items: prefetched primero (instantáneo), edge function como fallback.
  useEffect(() => {
    if (!isOpen || !orderId || !orderToken) {
      setProducts([]);
      setError(null);
      return;
    }
    if (prefetchedItems && prefetchedItems.length > 0) {
      setProducts(prefetchedItems as OrderProduct[]);
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      const { data, error: invokeErr } = await supabase.functions.invoke('whatsapp-send-message', {
        body: {
          action: 'get_order_details',
          conversation_id: conversationId,
          order_id: orderId,
          order_token: orderToken,
        },
      });
      if (cancelled) return;
      if (invokeErr) {
        setError(invokeErr.message ?? 'Error al cargar el detalle del pedido');
        setLoading(false);
        return;
      }
      const result = data as { success?: boolean; products?: OrderProduct[]; reason?: string } | null;
      if (!result?.success) {
        setError(result?.reason ?? 'No se pudo obtener el detalle del pedido');
        setProducts([]);
        setLoading(false);
        return;
      }
      setProducts(result.products ?? []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [isOpen, orderId, orderToken, conversationId, prefetchedItems]);

  const customerLabel = customer?.name ?? customer?.phone ?? 'Cliente';
  const totalLabel = formatTotalAmount1000(totalAmount1000, currency);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Solicitud de pedido"
      primaryAction={{
        content: 'Crear pedido COD en Smarty',
        disabled: products.length === 0 || loading || !onCreateInternalOrder,
        helpText: onCreateInternalOrder
          ? 'Pre-carga estos items en el creador de pedido COD'
          : undefined,
        onAction: () => {
          if (onCreateInternalOrder) {
            onCreateInternalOrder({ items: products, totalAmount1000, currency });
            onClose();
          }
        },
      }}
      secondaryActions={[
        {
          // Baileys NO soporta "Aceptar pedido" formal (sendOrderStatus no
          // existe en el SDK, verificado contra business.ts y messages-send.ts).
          // Alternativa profesional: enviar mensaje de texto pre-formateado.
          // El cliente recibe la confirmación clara, equivalente comunicacional.
          content: 'Responder al cliente',
          disabled: !onSendQuickReply || products.length === 0 || loading,
          onAction: () => {
            if (!onSendQuickReply) return;
            const totalStr = totalLabel ? ` de ${totalLabel}` : '';
            const qtyStr = products.length > 0
              ? ` por ${products.length} artículo${products.length === 1 ? '' : 's'}`
              : '';
            const text = `✅ Recibimos tu pedido${totalStr}${qtyStr}. Lo confirmamos a la brevedad. ¡Gracias por elegirnos!`;
            onSendQuickReply(text);
            onClose();
          },
        },
        { content: 'Cerrar', onAction: onClose },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {/* From: avatar + nombre + fecha */}
          <InlineStack gap="300" blockAlign="center" wrap={false}>
            <Box minWidth="40px">
              <Text as="span" tone="subdued" variant="bodySm">From</Text>
            </Box>
            <Avatar
              source={customer?.avatar ?? undefined}
              name={customerLabel}
              size="md"
              initials={customerLabel.charAt(0).toUpperCase()}
            />
            <BlockStack gap="050">
              <Text as="p" variant="bodyMd" fontWeight="semibold">{customerLabel}</Text>
              <Text as="p" variant="bodySm" tone="subdued">{formatOrderDate(orderDate)}</Text>
            </BlockStack>
          </InlineStack>

          <Divider />

          {/* Items */}
          {loading ? (
            <BlockStack gap="200">
              <InlineStack gap="300" blockAlign="center">
                <SkeletonThumbnail size="medium" />
                <Box width="60%"><SkeletonBodyText lines={2} /></Box>
              </InlineStack>
              <InlineStack align="center" gap="200" blockAlign="center">
                <Spinner size="small" />
                <Text as="span" tone="subdued" variant="bodySm">
                  Consultando WhatsApp (primera vez puede tardar 5-15s)
                </Text>
              </InlineStack>
            </BlockStack>
          ) : error ? (
            <Banner tone="critical"><Text as="p">{error}</Text></Banner>
          ) : products.length === 0 ? (
            <Banner tone="info"><Text as="p">No se encontraron items en este pedido.</Text></Banner>
          ) : (
            <BlockStack gap="300">
              {products.map((p, idx) => {
                const lineTotal = (() => {
                  const priceN = typeof p.price === 'number' ? p.price : Number(p.price ?? 0);
                  const qty = p.quantity ?? 1;
                  if (!Number.isFinite(priceN) || priceN <= 0) return '';
                  return formatPrice(priceN * qty, p.currency);
                })();
                return (
                  <Box
                    key={p.id ?? p.retailerId ?? idx}
                    padding="300"
                    background="bg-surface-secondary"
                    borderRadius="200"
                  >
                    <InlineStack gap="300" blockAlign="center" wrap={false} align="space-between">
                      <InlineStack gap="300" blockAlign="center" wrap={false}>
                        <OrderItemThumbnail
                          src={p.imageUrl}
                          alt={p.name ?? 'Producto'}
                        />
                        <BlockStack gap="050">
                          <Text as="p" variant="bodyMd" fontWeight="semibold">
                            {p.name ?? 'Producto'}
                          </Text>
                          <Text as="p" variant="bodySm" tone="subdued">
                            Cantidad: {p.quantity ?? 1}
                          </Text>
                        </BlockStack>
                      </InlineStack>
                      <Text as="p" variant="bodyMd" fontWeight="semibold" alignment="end">
                        {lineTotal}
                      </Text>
                    </InlineStack>
                  </Box>
                );
              })}
            </BlockStack>
          )}

          {/* Total */}
          {!loading && !error && products.length > 0 && totalLabel ? (
            <>
              <Divider />
              <InlineStack align="space-between" blockAlign="center">
                <Text as="p" variant="headingMd">Total</Text>
                <Text as="p" variant="headingLg">{totalLabel}</Text>
              </InlineStack>
            </>
          ) : null}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
